import { JitsiLocalTrack } from 'lib-jitsi-meet'
import { SagaIterator } from 'redux-saga'
import { put, takeLatest, select, all, take } from 'redux-saga/effects'

import { accountSelectors } from '@tabeeb/modules/account'
import { muteVideo } from '@tabeeb/modules/presentationToolbar/actions/muteVideo'
import { muteAudio } from '@tabeeb/modules/presentationToolbar/actions/muteAudio'
import { presentationSelectors } from '@tabeeb/modules/presentation'
import { gridViewSelectors } from '@tabeeb/modules/gridView'
import { AUDIO_DEVICE, AUDIO_MUTED, VIDEO_DEVICE, VIDEO_MUTED } from '@tabeeb/modules/presentation/constants'
import { removeLocalTrack, trackStopped, updateLocalTrack } from '@tabeeb/modules/presentation/actions/tracks'

import * as jitsiTrackActions from '../actions/jitsiTrack'
import * as conferenceActions from '../actions/conference'
import * as connectionActions from '../actions/connection'
import * as trackService from '../services/trackService'
import { usersActions } from '../../../users'
import { getConferenceTracksState } from '../selectors'

function* addTrackToRoom({ payload: track }: ReturnType<typeof jitsiTrackActions.addTrackToRoom>): SagaIterator {
  const jitsiRoom: ReturnType<typeof presentationSelectors.getJitsiConference> = yield select(
    presentationSelectors.getJitsiConference
  )

  if (!jitsiRoom) {
    track.dispose()
    return
  }

  jitsiRoom.addTrack(track)
}

function detachTrack({ payload: track }: ReturnType<typeof jitsiTrackActions.detachTrack>) {
  const { containers } = track
  if (containers.length > 0) {
    for (const container of containers) {
      track.detach(container)
    }
  }
}

function* stopTrack({ payload: track }: ReturnType<typeof jitsiTrackActions.stopTrack>): SagaIterator {
  yield put(jitsiTrackActions.detachTrack(track))
  track.dispose()
  yield put(trackStopped())
}

function* attachVideo({ payload: userId }: ReturnType<typeof jitsiTrackActions.attachVideo>): SagaIterator {
  const tracksState: ReturnType<typeof getConferenceTracksState> = yield select(getConferenceTracksState)
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  const track = trackService.getTrack(tracksState, userId, currentUserId, VIDEO_DEVICE)
  if (!track) {
    return
  }

  const isLargeVideo = tracksState.largeVideoUser !== null && tracksState.largeVideoUser.id === userId
  const isGridView: ReturnType<typeof gridViewSelectors.getIsGridViewActive> = yield select(
    gridViewSelectors.getIsGridViewActive
  )
  const isCallPreviewOpen: ReturnType<typeof presentationSelectors.getIsCallPreviewOpen> = yield select(
    presentationSelectors.getIsCallPreviewOpen
  )

  let container: HTMLVideoElement | null = null
  if (isLargeVideo) {
    container = trackService.getLargeVideoContainer()
  } else if (isGridView) {
    container = trackService.getGridVideoContainerById(userId)
  } else if (isCallPreviewOpen) {
    container = trackService.getCallPreviewVideoContainerById(userId)
  } else {
    container = trackService.getVideoContainerById(userId)
  }

  yield put(jitsiTrackActions.detachTrack(track))

  if (container) {
    if (isCallPreviewOpen) {
      const parsedBooleanVideoMuted = JSON.parse(localStorage.getItem(VIDEO_MUTED) ?? 'false')
      if (parsedBooleanVideoMuted) {
        yield put(muteVideo())
      }
    }

    track.attach(container)
  }

  yield put(usersActions.resetVideoLoadForUser(userId))
}

function* attachAudio({
  payload: { userId, container, trackToAttach },
}: ReturnType<typeof jitsiTrackActions.attachAudio>): SagaIterator {
  const tracksState: ReturnType<typeof getConferenceTracksState> = yield select(getConferenceTracksState)
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  let track = trackService.getTrack(tracksState, userId, currentUserId, AUDIO_DEVICE)
  if (!track) {
    return
  }

  let audioContainer = container

  const isCallPreviewOpen: ReturnType<typeof presentationSelectors.getIsCallPreviewOpen> = yield select(
    presentationSelectors.getIsCallPreviewOpen
  )
  if (isCallPreviewOpen) {
    audioContainer = trackService.getCallPreviewAudioContainerById(userId)
  }

  if (trackToAttach && trackToAttach.track.id !== track.track.id) {
    if (track.isLocal()) {
      yield put(removeLocalTrack(track as JitsiLocalTrack))
    }

    yield put(jitsiTrackActions.stopTrack(track))
    yield take(trackStopped)

    if (trackToAttach.isLocal()) {
      yield put(updateLocalTrack(trackToAttach as JitsiLocalTrack))
    }

    track = trackToAttach
  }

  if (audioContainer) {
    if (isCallPreviewOpen) {
      const parsedBooleanAudioMuted = JSON.parse(localStorage.getItem(AUDIO_MUTED) ?? 'false')
      if (parsedBooleanAudioMuted) {
        yield put(muteAudio())
      }
    }

    track.attach(audioContainer)
    if (track.isLocal()) {
      audioContainer.pause()
    } else {
      audioContainer.play()
    }
  }
}

function* enterRoom(): SagaIterator {
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )
  yield put(usersActions.setVideoLoadForUser(currentUserId))
}

function* onUserJoined({ payload: [participantId] }: ReturnType<typeof conferenceActions.onUserJoined>): SagaIterator {
  const userId: ReturnType<typeof presentationSelectors.getUserIdByParticipantId> = yield select((state) =>
    presentationSelectors.getUserIdByParticipantId(state, participantId)
  )
  const currentUserId: ReturnType<typeof accountSelectors.getCurrentUserId> = yield select(
    accountSelectors.getCurrentUserId
  )

  if (userId !== currentUserId) {
    yield put(usersActions.setVideoLoadForUser(userId))
  }
}
function* jitsiTrackSaga() {
  yield all([
    takeLatest(jitsiTrackActions.addTrackToRoom, addTrackToRoom),
    takeLatest(jitsiTrackActions.detachTrack, detachTrack),
    takeLatest(jitsiTrackActions.stopTrack, stopTrack),
    takeLatest(jitsiTrackActions.attachVideo, attachVideo),
    takeLatest(jitsiTrackActions.attachAudio, attachAudio),
    takeLatest(connectionActions.enterRoom, enterRoom),
    takeLatest(conferenceActions.onUserJoined, onUserJoined),
  ])
}

export default jitsiTrackSaga
